import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Mail, Lock, Sparkles, ArrowRight, Zap, Users, BarChart3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const schema = z.object({
    email: z.string().email(t('errors.invalidEmail')),
    password: z.string().min(1, t('errors.required'))
  });

  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const result = await login(data.email, data.password);
      toast.success(t('auth.welcome'));

      // Redirect to onboarding if not completed
      if (result?.needsOnboarding) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      toast.error(error.message || t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        {t('auth.welcome')}
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        {t('auth.loginSubtitle') || 'Entre na sua conta para continuar'}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Input
          label={t('auth.email')}
          type="email"
          placeholder="email@exemplo.com"
          leftIcon={<Mail className="w-5 h-5" />}
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label={t('auth.password')}
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          leftIcon={<Lock className="w-5 h-5" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="focus:outline-none"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          }
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 dark:bg-gray-800"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('auth.rememberMe')}</span>
          </label>
          <Link
            to="/forgot-password"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>

        <Button
          type="submit"
          className="w-full"
          size="lg"
          isLoading={isLoading}
        >
          {t('auth.login')}
        </Button>
      </form>

      {/* Signup CTA Card */}
      <div className="mt-8 relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-6 text-white shadow-xl">
        {/* Background decoration */}
        <div className="absolute -right-8 -top-8 h-20 w-20 sm:h-32 sm:w-32 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 h-20 w-20 sm:h-32 sm:w-32 rounded-full bg-white/10 blur-2xl" />

        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-yellow-300" />
            <span className="text-sm font-semibold text-primary-100">
              {t('auth.signupPromo.badge') || 'Novo por aqui?'}
            </span>
          </div>

          <h3 className="text-xl font-bold mb-2">
            {t('auth.signupPromo.title') || 'Comece grátis por 7 dias'}
          </h3>

          <p className="text-primary-100 text-sm mb-4">
            {t('auth.signupPromo.subtitle') || 'Crie sua conta e tenha acesso a todas as funcionalidades'}
          </p>

          {/* Mini features */}
          <div className="flex flex-wrap gap-3 mb-5">
            <div className="flex items-center gap-1.5 text-xs bg-white/15 rounded-full px-3 py-1.5">
              <Zap className="w-3.5 h-3.5 text-yellow-300" />
              <span>{t('auth.signupPromo.feature1') || 'Landing pages'}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs bg-white/15 rounded-full px-3 py-1.5">
              <BarChart3 className="w-3.5 h-3.5 text-green-300" />
              <span>{t('auth.signupPromo.feature2') || 'Análise IA'}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs bg-white/15 rounded-full px-3 py-1.5">
              <Users className="w-3.5 h-3.5 text-blue-300" />
              <span>{t('auth.signupPromo.feature3') || 'Automações'}</span>
            </div>
          </div>

          <Link
            to="/register"
            className="group flex items-center justify-center gap-2 w-full bg-white text-primary-700 font-bold py-3 px-6 rounded-xl hover:bg-primary-50 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {t('auth.signUpNow') || 'Criar conta grátis'}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>

          <p className="text-center text-xs text-primary-200 mt-3">
            {t('auth.signupPromo.noCreditCard') || '✓ Sem cartão de crédito'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
